/**
 * Deeplink environment detection + safe opening.
 *
 * Decline UI action buttons (and, later, web prequal) open `tamara.go.link`
 * universal links that route into the Tamara native app. Those links only lead
 * somewhere useful in some environments, so we gate the buttons on
 * {@link canOpenDeeplink}. See CXO-2770 and the "isDeeplinkWorking()" research
 * thread.
 *
 * Rules (kept intentionally conservative):
 * - Desktop browsers: no Tamara app to open — hidden.
 * - Embedded merchant iframes (e.g. NOON checkout): universal links can't break
 *   out of a cross-origin frame — hidden.
 * - Tamara app in-app WebView: a top-level navigation to the OneLink stays
 *   inside the same WebView (does not hand off to native / close the sheet),
 *   so the user gets stuck — hidden until we have a native bridge handoff.
 * - Mobile browsers and merchant in-app browsers: the universal link can
 *   route to the app (or its web fallback) — shown.
 */

/**
 * Only these hosts are accepted as action deeplinks (defense in depth).
 * `tamara.go.link` is the production AppsFlyer OneLink host; `tamarastg.go.link`
 * is its staging counterpart (the backend sends staging links in non-prod).
 */
const ALLOWED_DEEPLINK_HOSTS = new Set([
  'tamara.go.link',
  'tamarastg.go.link',
]);

const MOBILE_USER_AGENT_REGEX =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

/** True when the Tamara native app has injected its JS bridge. */
export function isTamaraAppWebView(): boolean {
  try {
    return typeof Mobile !== 'undefined' && Mobile != null;
  } catch {
    return false;
  }
}

/** True when running inside an iframe (incl. cross-origin merchant checkout). */
export function isEmbeddedInIframe(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — that only happens inside an
    // iframe, so treat it as embedded.
    return true;
  }
}

/** Best-effort mobile detection; the app WebView always counts as mobile. */
export function isMobileDevice(): boolean {
  if (isTamaraAppWebView()) {
    return true;
  }
  if (typeof navigator === 'undefined') {
    return false;
  }
  return MOBILE_USER_AGENT_REGEX.test(navigator.userAgent ?? '');
}

/**
 * Whether an app deeplink can meaningfully open here.
 *
 * Requires a top-level mobile browser (or merchant IAB). Excludes:
 * - desktop (no app to open)
 * - embedded iframes (can't break out)
 * - Tamara in-app WebView (`Mobile` bridge present) — navigating to a OneLink
 *   here does not leave / close the WebView, so CTAs would trap the user
 */
export function canOpenDeeplink(): boolean {
  return (
    isMobileDevice() && !isEmbeddedInIframe() && !isTamaraAppWebView()
  );
}

/** Validates a deeplink is an https URL on an allowed Tamara host. */
export function isValidDeeplinkUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      ALLOWED_DEEPLINK_HOSTS.has(parsed.hostname.toLowerCase())
    );
  } catch {
    return false;
  }
}

/**
 * Navigates the top-level document to a validated deeplink. Returns false
 * without navigating for invalid URLs.
 *
 * We use a top-level navigation (not `window.open('_blank')`): universal links
 * only route to the native app on a top-level navigation, and `window.open`
 * with `_blank` is commonly blocked or silently returns a dummy window inside
 * mobile in-app browsers, which would make the CTA a no-op. Callers gate on
 * {@link canOpenDeeplink}, which already excludes embedded iframes and the
 * Tamara app WebView, so `window.location` here is a top-level mobile browser.
 */
export function openDeeplink(url: string): boolean {
  if (!isValidDeeplinkUrl(url) || typeof window === 'undefined') {
    return false;
  }

  window.location.assign(url);
  return true;
}
